class PurchaseOptionsComponent extends HTMLElement {
  constructor() {
    super();
    this.boundHandleVariantChange = this.handleVariantChange.bind(this);
    
    // --- INIT DATA ---
    try {
      this.variantPrice = parseFloat(this.getAttribute('data-variant-price')); 
      this.compareAtPrice = parseFloat(this.getAttribute('data-compare-at-price')) || null;
      this.currencyCode = this.getAttribute('data-currency-code') || 'USD';
      // Use Intl for robust currency formatting (handles JPY, EUR, etc correctly)
      this.currencyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.currencyCode,
      });

      const rawPlans = this.getAttribute('data-selling-plans');
      let parsedPlans = rawPlans ? JSON.parse(rawPlans) : [];
      if (parsedPlans && !Array.isArray(parsedPlans)) parsedPlans = [parsedPlans];
      this.allPlans = parsedPlans.flatMap(group => group.selling_plans);
      
    } catch (e) {
      console.error("PurchaseOptions: Error parsing plans", e);
      this.allPlans = [];
    }

    // Read metafield-based default plan ID
    this.defaultPlanId = this.getAttribute('data-default-plan-id') || null;
    
    const handle = this.getAttribute('data-product-handle');

    // Bundle product detection from metafield
    const isBundleAttr = this.getAttribute('data-is-bundle');
    this.isBundle = isBundleAttr === 'true'
      || isBundleAttr === true
      || handle === 'mobility-protocol-1'
      || handle === 'bulletproof-protocol'
      || handle === 'performance-protocol-1'
      || handle === 'longevity-protocol-1';
    const bundleSubscriptionsEnabledAttr = this.getAttribute('data-bundle-subscriptions-enabled');
    this.bundleSubscriptionsEnabled =
      bundleSubscriptionsEnabledAttr === 'true'
      || this.allPlans.length > 0;
    this.bundleText = this.getAttribute('data-bundle-text') || '';
    
    // Bundle pricing: variant price is already discounted (15% off)
    // Calculate original price: discounted_price / (1 - discount_rate)
    // 15% discount means current price = 85% of original, so original = current / 0.85
    this.bundleOriginalPrice = this.resolveBundleOriginalPrice(handle);
    
    // GLOW is sold as one selected pack at a time. The pack itself can contain
    // either 15 or 30 daily sachets, so supply/pricing copy must follow the
    // selected Shopify variant rather than assume every box is a 30 pack.
    this.isGlow = (handle === 'beme-liquid-collagen-for-hair-skin-nails');
    this.oneBoxOnly = this.isGlow;
    this.currentVariant = null;
    
    this.initPlanSelection();
    // Remember the customer's eligible 30-pack preference. Selecting the
    // 15-day starter temporarily forces one-time purchase, but switching back
    // to 30 sachets should restore the previous 30-pack choice.
    this.glowPreferredOption = this.selectedOption;
    this.glowForcedOneTime = false;
    // The full 30-day formula supply is the default so Subscribe and One-time
    // are compared on the same quantity. The one-box option is explicitly a
    // 15-day starter and therefore remains one-time only.
    this.selectedQuantity = (this.isBundle || this.oneBoxOnly) ? 1 : 2;
    this.fullSupplyPreferredOption = this.selectedOption;
  }

  initPlanSelection() {
    let correctPlan = null;

    // Show protocol Subscribe & Save whenever Shopify exposes an eligible
    // selling plan. The metafield remains an optional explicit override.
    if (this.isBundle && !this.bundleSubscriptionsEnabled) {
      this.selectedOption = 'onetime';
      this.currentPlanId = null;
      this.currentPlanDiscounts = [];
      return;
    }

    // Strategy 1: Use metafield-defined selling plan ID if provided
    if (this.defaultPlanId && this.allPlans.length > 0) {
      // Try to find plan by ID (convert to string for comparison)
      correctPlan = this.allPlans.find(p => String(p.id) === String(this.defaultPlanId));
    }

    // Strategy 2 (Fallback): Use the first available selling plan
    if (!correctPlan && this.allPlans.length > 0) {
      correctPlan = this.allPlans[0];
      console.log("PurchaseOptions: Using fallback to first plan", correctPlan.name);
    }

    if (correctPlan) {
      this.selectedOption = 'subscription';
      this.currentPlanId = correctPlan.id; 
      this.currentPlanDiscounts = correctPlan.price_adjustments;
    } else {
      this.selectedOption = 'onetime';
      this.currentPlanId = null;
    }
  }


  connectedCallback() {
    this.applyGlowPackStrategy(this.getGlowPackSize());
    this.render();
    this.updateGlowPackUnitPrices();
    this.attachEventListeners(); // Initial attach
    this.updateExternalElements(); // Updates form and main price

    // Wonder publishes variant changes through its pub/sub channel. Keep the
    // DOM listener only as a compatibility fallback for other theme contexts.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.boundHandleVariantChange);
    } else {
      this.usesDomVariantEvent = true;
      document.addEventListener('variant:change', this.boundHandleVariantChange);
    }
  }

  disconnectedCallback() {
    if (typeof this.unsubscribeVariantChange === 'function') this.unsubscribeVariantChange();
    if (this.usesDomVariantEvent) {
      document.removeEventListener('variant:change', this.boundHandleVariantChange);
    }
  }

  handleVariantChange(payload) {
    const variant = payload?.data?.variant || payload?.detail?.variant || payload?.variant;
    const nextPrice = Number(variant?.price);
    if (!variant || !Number.isFinite(nextPrice)) return;

    this.currentVariant = variant;
    this.variantPrice = nextPrice;
    this.compareAtPrice = Number(variant.compare_at_price) || null;
    this.bundleOriginalPrice = this.resolveBundleOriginalPrice(this.getAttribute('data-product-handle'));
    this.applyGlowPackStrategy(this.getGlowPackSize());
    this.render();
    this.updateGlowPackUnitPrices();
    this.attachEventListeners();
    this.updateExternalElements();
  }

  resolveBundleOriginalPrice(handle) {
    if (!this.isBundle) return null;
    if (Number.isFinite(this.compareAtPrice) && this.compareAtPrice > this.variantPrice) {
      return this.compareAtPrice;
    }

    // US component retail: Mobility is 2 × PROTECT ($35) + 2 × MOVE ($45) = $160;
    // Bulletproof is 2 × PROTECT + 2 × BUILD ($50) = $170; Performance is all
    // three formulas, bringing its six-box component value to $260.
    // Other markets retain the existing 15%-bundle-value fallback so their
    // localized currency pricing is never replaced with a hard-coded USD sum.
    if (handle === 'mobility-protocol-1' && this.currencyCode === 'USD') return 16000;
    if (handle === 'bulletproof-protocol' && this.currencyCode === 'USD') return 17000;
    if (handle === 'performance-protocol-1' && this.currencyCode === 'USD') return 26000;
    if (handle === 'longevity-protocol-1' && this.currencyCode === 'USD') return 13500;

    const BUNDLE_ONETIME_DISCOUNT = 0.15;
    return Math.round(this.variantPrice / (1 - BUNDLE_ONETIME_DISCOUNT));
  }

  discountPercent(referencePrice, sellingPrice) {
    if (!Number.isFinite(referencePrice) || referencePrice <= 0 || sellingPrice >= referencePrice) return 0;
    const rawPercent = ((referencePrice - sellingPrice) / referencePrice) * 100;
    const nearestWhole = Math.round(rawPercent);
    // Preserve conservative "save at least" claims, while correcting tiny
    // floating-point drift when the displayed prices are an exact percentage.
    return Math.abs(rawPercent - nearestWhole) < 0.01
      ? nearestWhole
      : Math.floor(rawPercent);
  }

  taxNote() {
    return this.currencyCode === 'USD' ? 'Taxes calculated at checkout.' : 'Tax included.';
  }

  // Plan-discounted price regardless of which option is selected
  // (used for the price shown on the Subscribe card itself)
  planPrice(basePrice) {
    if (!this.currentPlanDiscounts || this.currentPlanDiscounts.length === 0) return basePrice;
    const adj = this.currentPlanDiscounts[0];
    const val = parseFloat(adj.value);
    switch (adj.value_type) {
        case 'percentage': return basePrice - (basePrice * (val / 100));
        case 'fixed_amount': return basePrice - val;
        case 'price': return val;
        default: return basePrice;
    }
  }

  // REFACTOR: Centralized Price Calculation Logic
  // Returns the calculated price in cents
  calculateCurrentPrice(basePrice) {
    if (this.selectedOption === 'onetime') return basePrice;
    if (!this.currentPlanDiscounts || this.currentPlanDiscounts.length === 0) return basePrice;

    const adj = this.currentPlanDiscounts[0];
    const val = parseFloat(adj.value);

    switch (adj.value_type) {
        case 'percentage': return basePrice - (basePrice * (val / 100));
        case 'fixed_amount': return basePrice - val;
        case 'price': return val; // Handles fixed price plans
        default: return basePrice;
    }
  }

  getGlowPackSize() {
    if (!this.isGlow) return null;

    const variantOptions = this.currentVariant && Array.isArray(this.currentVariant.options)
      ? this.currentVariant.options.join(' ')
      : '';
    let match = variantOptions.match(/(?:^|\D)(15|30)(?:\D|$)/);

    if (!match) {
      const checked = Array.from(document.querySelectorAll('variant-options input[type="radio"]:checked')).find((input) =>
        /sachet|pack|count/i.test(input.name || '')
      );
      match = String(checked ? checked.value : '').match(/(?:^|\D)(15|30)(?:\D|$)/);
    }

    // The existing GLOW product is a 30 pack. This fallback only covers the
    // initial render before Wonder dispatches its first variant event.
    return match ? parseInt(match[1], 10) : 30;
  }

  applyGlowPackStrategy(packSize) {
    if (!this.isGlow) return;

    if (packSize === 15) {
      if (!this.glowForcedOneTime) this.glowPreferredOption = this.selectedOption;
      this.glowForcedOneTime = true;
      this.selectedOption = 'onetime';
      this.selectedQuantity = 1;
      return;
    }

    if (packSize === 30 && this.glowForcedOneTime) {
      this.glowForcedOneTime = false;
      this.selectedOption = this.glowPreferredOption === 'onetime' ? 'onetime' : 'subscription';
    }
  }

  render() {
    const isCore = !!this.closest('[data-beme-clarity]');
    const showDetails = this.selectedOption === 'subscription' ? 'block' : 'none';
    const isProtect = this.getAttribute('data-product-handle') === 'beme-liquid-collagen-peptides-for-ligaments-tendons';
    
    // Use centralized calculation
    const discountedPrice = this.calculateCurrentPrice(this.variantPrice);
    const subscriptionReferencePrice = (this.isBundle && this.bundleOriginalPrice)
      ? this.bundleOriginalPrice
      : this.variantPrice;
    const subscriptionDiscountPercent = this.discountPercent(subscriptionReferencePrice, discountedPrice);
    const oneTimeDiscountPercent = (this.isBundle && this.bundleOriginalPrice)
      ? this.discountPercent(this.bundleOriginalPrice, this.variantPrice)
      : 0;
    const incrementalSubscriptionDiscountPercent = discountedPrice < this.variantPrice
      ? Math.round(((this.variantPrice - discountedPrice) / this.variantPrice) * 100)
      : 0;
    const subscriptionDiscountText = this.isBundle
      ? `SAVE ${subscriptionDiscountPercent || 15}% TOTAL`
      : `SAVE ${subscriptionDiscountPercent || 15}%`;
    const oneTimeDiscountText = this.isBundle && oneTimeDiscountPercent
      ? `SAVE ${oneTimeDiscountPercent}% BUNDLE`
      : null;

    // Daily price follows the pack selected in the Shopify variant picker.
    const glowPackSize = this.getGlowPackSize();
    const daysPerUnit = this.isBundle ? 30 : (this.isGlow ? glowPackSize : 15);
    const dailyPriceCents = discountedPrice / daysPerUnit;
    const isGlowStarter = this.isGlow && glowPackSize === 15;
    const isFormulaStarter = !this.isBundle && !this.isGlow && this.selectedQuantity === 1;
    
    const singleBoxLabel = this.isGlow ? `${glowPackSize}-DAY SUPPLY` : "15-DAY SUPPLY";

    // Supply is selected independently from purchase type. This keeps the
    // 30-day Subscribe and One-time prices directly comparable.
    let supplySelectorHtml = '';
    
    if (this.isBundle) {
      const bundleContentsByHandle = {
        'mobility-protocol-1': '4 boxes · 60 sachets',
        'bulletproof-protocol': '4 boxes · 60 sachets',
        'performance-protocol-1': '6 boxes · 90 sachets',
        'longevity-protocol-1': '3 boxes · 60 sachets',
      };
      // Canonical protocol counts take priority over any older bundle metafield
      // copy so the purchase card always matches the actual 30-day contents.
      const bundleContents = bundleContentsByHandle[this.getAttribute('data-product-handle')] || this.bundleText || '';
      const bundleSubtext = bundleContents ? `<span class="btn-subtext">${bundleContents}</span>` : '';
      supplySelectorHtml = `
        <div class="purchase-supply-summary">
          <span class="purchase-supply-summary__label">30-day protocol supply</span>
          <span>${bundleSubtext}</span>
        </div>`;
    } else if (this.isGlow) {
      supplySelectorHtml = `
        <div class="purchase-supply-summary">
          <span class="purchase-supply-summary__label">${glowPackSize} pouches</span>
          <span>1 box · ${glowPackSize} days at 1 daily</span>
        </div>`;
    } else {
      supplySelectorHtml = `
        <div class="purchase-supply-picker">
          <div class="purchase-supply-picker__label">${isCore ? 'Choose your supply' : '1. Choose your supply'}</div>
          <div class="plan-buttons-wrapper">
            <button type="button" class="freq-button ${this.selectedQuantity === 2 ? 'active' : ''}" data-qty="2" aria-pressed="${this.selectedQuantity === 2}">
                <div class="btn-sub">${isCore ? '30-day supply' : (isProtect ? 'FULL 30-DAY ROUTINE' : '★ RECOMMENDED')}</div>
                <div class="btn-main">${isCore ? '30 pouches' : '30-day routine'}</div>
                <div class="btn-subtext">${isCore ? '2 boxes · 30 days at 1 daily' : '2 boxes · 30 sachets'}</div>
            </button>
            <button type="button" class="freq-button ${this.selectedQuantity === 1 ? 'active' : ''}" data-qty="1" aria-pressed="${this.selectedQuantity === 1}">
                <div class="btn-sub">${isCore ? '15-day starter' : 'ONE-TIME STARTER'}</div>
                <div class="btn-main">${isCore ? '15 pouches' : singleBoxLabel}</div>
                <div class="btn-subtext">${isCore ? '1 box · 15 days at 1 daily' : '1 box · 15 sachets'}</div>
            </button>
          </div>
        </div>`;
    }

    // Footer info - different content for bundles
    let footerInfoHtml = '';
    if (this.isBundle) {
      footerInfoHtml = `
        <div class="subscription-footer-info">
           <p><strong>${this.formatMoney(dailyPriceCents)}</strong> per day</p>
        </div>
      `;
    } else {
      const deliveryDays = this.isGlow ? glowPackSize : (15 * this.selectedQuantity);
      const deliveryTotal = discountedPrice * this.selectedQuantity;
      const boxBreakdown = this.selectedQuantity === 1
        ? `1 box · ${this.formatMoney(discountedPrice)} per box`
        : `${this.selectedQuantity} boxes · ${this.formatMoney(discountedPrice)} per box`;
      footerInfoHtml = `
        <div class="subscription-footer-info">
           <p class="subscription-delivery-total"><strong>${this.formatMoney(deliveryTotal)}</strong> per delivery</p>
           <p>${boxBreakdown}</p>
           ${isCore ? `<p class="subscription-renewal">${this.deliveryIntervalLabel()}</p>` : ''}
           <p><strong>${this.formatMoney(dailyPriceCents)}</strong> per day</p>
        </div>
      `;
    }

    // Card prices (design: price on each selectable card, sub shows strikethrough)
    const displayQuantity = (this.isBundle || this.isGlow) ? 1 : this.selectedQuantity;
    const subCardPrice = this.planPrice(this.variantPrice) * displayQuantity;
    const subComparePrice = (this.isBundle && this.bundleOriginalPrice)
      ? this.bundleOriginalPrice
      : this.variantPrice * displayQuantity;
    const subPriceHtml = subCardPrice < subComparePrice
      ? `<span class="option-price">${this.formatMoney(subCardPrice)} <s>${this.formatMoney(subComparePrice)}</s></span>`
      : `<span class="option-price">${this.formatMoney(subCardPrice)}</span>`;
    const onetimePriceHtml = (this.isBundle && this.bundleOriginalPrice)
      ? `<span class="option-price">${this.formatMoney(this.variantPrice)} <s>${this.formatMoney(this.bundleOriginalPrice)}</s></span>`
      : `<span class="option-price">${this.formatMoney(this.variantPrice * displayQuantity)}</span>`;

    if (isGlowStarter) {
      this.innerHTML = `
        <div class="purchase-options-container purchase-options-container--starter">
          <div class="option-box onetime active glow-starter-option">
            <div class="option-popular-badge glow-starter-option__badge">One-time only</div>
            <label class="option-label">
              <div class="radio-wrapper">
                <input type="radio" name="purchase_option_ui" value="onetime" checked>
                <span>15-Day Starter Pack</span>
              </div>
              <span class="option-label-right">${onetimePriceHtml}</span>
            </label>
            <div class="glow-starter-option__details">
              <strong>Try the taste, format and daily routine.</strong>
              <span>15 pouches · 20 mL each. One-time purchase.</span>
            </div>
          </div>
        </div>
      `;
      return;
    }

    if (isFormulaStarter) {
      this.innerHTML = `
        <div class="purchase-options-container purchase-options-container--starter">
          ${supplySelectorHtml}
          <div class="purchase-supply-picker__label">${isCore ? 'Purchase option' : '2. Purchase option'}</div>
          <div class="option-box onetime active glow-starter-option">
            <div class="option-popular-badge glow-starter-option__badge">One-time only</div>
            <label class="option-label">
              <div class="radio-wrapper">
                <input type="radio" name="purchase_option_ui" value="onetime" checked>
                <span>15-Day Starter</span>
              </div>
              <span class="option-label-right">${onetimePriceHtml}</span>
            </label>
            <div class="glow-starter-option__details">
              <strong>Try the flavor, format and daily routine.</strong>
              <span>15 pouches in one box. One-time purchase; no result is promised on a fixed timeline.</span>
            </div>
          </div>
        </div>
      `;
      return;
    }

    if (this.isBundle && !this.bundleSubscriptionsEnabled) {
      this.innerHTML = `
        <div class="purchase-options-container purchase-options-container--protocol-onetime">
          ${supplySelectorHtml}
          <div class="option-box onetime active">
            <label class="option-label">
              <div class="radio-wrapper">
                <input type="radio" name="purchase_option_ui" value="onetime" checked>
                <span>One-time Protocol Purchase</span>
              </div>
              <span class="option-label-right">${onetimePriceHtml}${oneTimeDiscountText ? `<span class="discount-badge">${oneTimeDiscountText}</span>` : ''}</span>
            </label>
            <div class="glow-starter-option__details">
              <strong>Complete 30-day protocol supply.</strong>
              <span>Includes the full component-box quantities shown above.</span>
            </div>
          </div>
        </div>
      `;
      return;
    }

    this.innerHTML = `
      <div class="purchase-options-container">
        ${supplySelectorHtml}
        <div class="purchase-supply-picker__label">${isCore ? 'Purchase option' : (this.isBundle || this.isGlow ? 'Choose your purchase option' : '2. Choose your purchase option')}</div>
        <div class="option-box subscription ${this.selectedOption === 'subscription' ? 'active' : ''}">
          <div class="option-popular-badge">${isCore ? subscriptionDiscountText.replace(/^SAVE /, 'Save ') : (isProtect ? subscriptionDiscountText : 'Most popular')}</div>
          <label class="option-label">
            <div class="radio-wrapper">
                <input type="radio" name="purchase_option_ui" value="subscription" ${this.selectedOption === 'subscription' ? 'checked' : ''}>
                <span>Subscribe & Save</span>
            </div>
            <span class="option-label-right">${subPriceHtml}${isCore || isProtect ? '' : `<span class="discount-badge">${subscriptionDiscountText}</span>`}</span>
          </label>
          
          <div class="subscription-details" style="display: ${showDetails}">
             <ul class="sub-benefits-list">
                ${this.renderBenefitItem(
                  this.isBundle
                    ? `Save ${subscriptionDiscountPercent || 15}% total vs separate boxes (${oneTimeDiscountPercent || 15}% protocol bundle + ${incrementalSubscriptionDiscountPercent || 12}% subscription saving)`
                    : `${subscriptionDiscountText} on every order`
                )}
                ${this.renderBenefitItem(`Free shipping on subscription orders`)}
                ${this.renderBenefitItem(`Cancel anytime`)}
             </ul>

             ${footerInfoHtml}
          </div>
        </div>

        <div class="option-box onetime ${this.selectedOption === 'onetime' ? 'active' : ''}">
          <label class="option-label">
             <div class="radio-wrapper">
                <input type="radio" name="purchase_option_ui" value="onetime" ${this.selectedOption === 'onetime' ? 'checked' : ''}>
                <span>One-time Purchase</span>
             </div>
             <span class="option-label-right">${onetimePriceHtml}${oneTimeDiscountText ? `<span class="discount-badge">${oneTimeDiscountText}</span>` : ''}</span>
          </label>
        </div>
      </div>
    `;
  }
  
  // Helper to keep render() clean
  renderBenefitItem(text) {
      return `
        <li>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            <span>${text}</span>
        </li>`;
  }

  publish(eventName, detail = {}) {
    const payload = {
      product_handle: this.getAttribute('data-product-handle'),
      currency: this.currencyCode,
      purchase_option: this.selectedOption,
      quantity: this.selectedQuantity || 1,
      value_cents: this.calculateCurrentPrice(this.variantPrice) * (this.selectedQuantity || 1),
      ...detail,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(`beme:${eventName}`, payload);
    }
  }

  attachEventListeners() {
    this.querySelectorAll('input[name="purchase_option_ui"]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.selectedOption = e.target.value;
        if (this.isGlow && this.getGlowPackSize() === 30) {
          this.glowPreferredOption = this.selectedOption;
        }
        if (!this.isBundle && !this.isGlow && this.selectedQuantity === 2) {
          this.fullSupplyPreferredOption = this.selectedOption;
        }
        this.publish('purchase_option_change');
        this.handleUpdate();
      });
    });

    this.querySelectorAll('.freq-button').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault(); 
            e.stopPropagation();
            const nextQuantity = parseInt(btn.getAttribute('data-qty'));
            this.selectedQuantity = nextQuantity;
            if (!this.isBundle && !this.isGlow) {
              if (nextQuantity === 1) {
                this.selectedOption = 'onetime';
              } else {
                this.selectedOption = this.fullSupplyPreferredOption || 'subscription';
              }
            }
            this.publish('supply_option_change', {
              supply_days: this.isBundle ? 30 : (this.isGlow ? this.getGlowPackSize() : 15 * nextQuantity),
            });
            this.handleUpdate();
        });
    });
  }

  handleUpdate() {
    // Rendering replaces the controls. Keep an existing focus in this purchase
    // group so keyboard selection can continue without jumping to the page.
    const activeControl = document.activeElement;
    let focusSelector = null;
    if (this.contains(activeControl)) {
      if (activeControl.matches('.freq-button')) {
        focusSelector = `.freq-button[data-qty="${activeControl.dataset.qty}"]`;
      } else if (activeControl.matches('input[name="purchase_option_ui"]')) {
        focusSelector = `input[name="purchase_option_ui"][value="${activeControl.value}"]`;
      }
    }
    this.render();
    this.updateGlowPackUnitPrices();
    this.attachEventListeners(); 
    this.updateExternalElements();
    if (focusSelector) this.querySelector(focusSelector)?.focus({ preventScroll: true });
  }

  updateGlowPackUnitPrices() {
    if (!this.isGlow) return;

    const picker = document.querySelector('variant-options');
    const variantJson = picker && picker.querySelector('script[type="application/json"]');
    if (!picker || !variantJson) return;

    let variants = [];
    try {
      variants = JSON.parse(variantJson.textContent || '[]');
    } catch (error) {
      return;
    }

    picker.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (!/sachet|pack|count/i.test(input.name || '')) return;
      const packMatch = String(input.value || '').match(/(?:^|\D)(15|30)(?:\D|$)/);
      if (!packMatch) return;

      const packSize = parseInt(packMatch[1], 10);
      const variant = variants.find((item) => Array.isArray(item.options) && item.options.includes(input.value));
      const price = Number(variant && variant.price);
      const label = picker.querySelector(`label[for="${input.id}"]`);
      const unit = label && label.querySelector('.beme-pack-option__unit');
      if (!Number.isFinite(price) || !unit) return;

      unit.textContent = `One-time: ${this.formatMoney(Math.round(price / packSize))}/pouch`;
    });
  }

  updateExternalElements() {
    this.updateFormInputs();
    this.updateMainDisplayPrice();
    this.updateAtcLabel();
    this.updateShippingCue();
    this.updateClaritySummary();
    this.dispatchEvent(new CustomEvent('beme:purchase-selection-change', {
      bubbles: true,
      detail: {
        productHandle: this.getAttribute('data-product-handle'),
        purchaseOption: this.selectedOption,
        quantity: this.selectedQuantity || 1,
      },
    }));
  }


  deliveryIntervalLabel() {
    const plan = this.allPlans.find(item => String(item.id) === String(this.currentPlanId));
    const value = plan?.options?.find(item => item.name === 'Order Frequency and Unit')?.value;
    const match = typeof value === 'string' && value.match(/^(\d+)-(day|week|month|year)$/);
    if (!match) return 'Review the delivery schedule at checkout.';
    const amount = Number(match[1]);
    const interval = amount === 1 ? match[2] : amount + ' ' + match[2] + 's';
    return 'Renews every ' + interval + '. Cancel at least 24 hours before the next shipment.';
  }

  updateClaritySummary() {
    const root = this.closest('[data-beme-clarity]');
    if (!root) return;
    const pack = this.isGlow ? this.getGlowPackSize() : 15;
    const boxes = this.selectedQuantity || 1;
    const count = pack * boxes;
    root.querySelectorAll('[data-beme-pouch-count]').forEach(el => { el.textContent = el.dataset.template.replace('__COUNT__', count); });
    root.querySelectorAll('[data-beme-box-count]').forEach(el => { el.textContent = boxes === 1 ? el.dataset.singular : el.dataset.plural.replace('__COUNT__', boxes); });
    root.querySelectorAll('[data-beme-daily-supply]').forEach(el => { el.textContent = el.dataset.template.replace('__COUNT__', count); });
    root.querySelectorAll('[data-beme-unit-price]').forEach(el => { el.textContent = el.dataset.template.replace('__PRICE__', this.formatMoney(this.calculateCurrentPrice(this.variantPrice) / pack)); });
    root.querySelectorAll('[data-beme-amazon-link]').forEach(el => {
      if (this.isGlow) el.href = 'https://www.amazon.com/dp/' + (pack === 15 ? 'B0F5QC5QFS' : 'B0F5Q7G7VQ');
    });
    root.querySelectorAll('[data-beme-amazon-pack]').forEach(el => { el.textContent = el.dataset.template.replace('__COUNT__', this.isGlow ? pack : 15); });
  }

  updateShippingCue() {
    if (this.getAttribute('data-product-handle') !== 'beme-liquid-collagen-peptides-for-ligaments-tendons') return;
    const cue = document.querySelector('[data-beme-selection-shipping]');
    if (!cue) return;
    const receivesFreeShipping = this.selectedOption === 'subscription' || (this.selectedQuantity || 1) === 2;
    cue.textContent = receivesFreeShipping
      ? 'Free standard U.S. shipping.'
      : 'Shipping calculated at checkout.';
  }

  // "Add to cart · $33.48" — reflects option + quantity, using the theme's
  // localized base label so translations are preserved.
  updateAtcLabel() {
    const formId = this.getAttribute('data-product-form-id');
    const form = document.getElementById(formId);
    if (!form) return;
    const btn = form.querySelector('.product-form__submit');
    if (!btn || btn.disabled) return;
    const span = btn.querySelector('span');
    if (!span) return;
    if (!this._atcBaseText) this._atcBaseText = span.textContent.trim();
    const unitPrice = this.calculateCurrentPrice(this.variantPrice);
    const total = unitPrice * (this.selectedQuantity || 1);
    const productHandle = this.getAttribute('data-product-handle');
    let actionText = this._atcBaseText;
    if (this.closest('[data-beme-clarity]')) {
      actionText = this.selectedOption === 'subscription' ? 'Start subscription' : this._atcBaseText;
    } else if (productHandle === 'beme-liquid-collagen-peptides-for-ligaments-tendons') {
      if ((this.selectedQuantity || 1) === 1) {
        actionText = 'Try the starter';
      } else if (this.selectedOption === 'subscription') {
        actionText = 'Start subscription';
      } else {
        actionText = 'Add 30-day supply';
      }
    }
    span.textContent = `${actionText} · ${this.formatMoney(total)}`;
  }

  updateFormInputs() {
    const formId = this.getAttribute('data-product-form-id');
    const form = document.getElementById(formId);
    if (!form) return;

    let planInput = form.querySelector('input[name="selling_plan"]');
    if (!planInput) {
      planInput = document.createElement('input');
      planInput.type = 'hidden';
      planInput.name = 'selling_plan';
      form.appendChild(planInput);
    }
    
    planInput.value = (this.selectedOption === 'subscription' && this.currentPlanId) ? this.currentPlanId : '';

    let qtyInput = form.querySelector('input[name="quantity"]');
    if (!qtyInput) {
      qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = 'quantity';
      form.appendChild(qtyInput);
    }
    qtyInput.value = this.selectedQuantity;

    // Trigger change for other scripts listening
    planInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updateMainDisplayPrice() {
    // Robust selector list
    const mainPriceContainer = document.querySelector('.wt-product__price') || 
                               document.querySelector('.product__price') || 
                               document.querySelector('[id^="price-"]');
                               
    if (!mainPriceContainer) return;

    const unitPrice = this.variantPrice;
    
    // REUSE the logic from render via the helper method
    const finalPrice = this.calculateCurrentPrice(unitPrice);
    
    if (this.selectedOption === 'onetime') {
        // Bundle one-time: show discounted price with strikethrough of original
        // Normal one-time: just show the regular price
        if (this.isBundle && this.bundleOriginalPrice) {
            mainPriceContainer.innerHTML = `
                <div class="price__subscription-display">
                    <div class="price__current-sub" style="font-weight: bold;">
                        ${this.formatMoney(unitPrice)} <span class="per-box">/ bundle</span>
                    </div>
                    <div class="price__was-sub" style="text-decoration: line-through; color: #888; margin-left: 10px;">
                        ${this.formatMoney(this.bundleOriginalPrice)}
                    </div>
                    <div class="price__tax-note">${this.taxNote()}</div>
                </div>
            `;
        } else {
            const quantity = this.selectedQuantity || 1;
            const deliveryDays = this.isGlow ? this.getGlowPackSize() : (15 * quantity);
            const deliveryTotal = unitPrice * quantity;
            const boxBreakdown = quantity === 1
                ? `1 box · ${this.formatMoney(unitPrice)} per box`
                : `${quantity} boxes · ${this.formatMoney(unitPrice)} per box`;
            mainPriceContainer.innerHTML = `
                <div class="price__subscription-display price__subscription-display--delivery">
                    <div class="price__current-sub">
                        ${this.formatMoney(deliveryTotal)} <span class="per-box">/ ${deliveryDays}-day supply</span>
                    </div>
                    <div class="beme-price-breakdown">${boxBreakdown}</div>
                    <div class="price__tax-note">${this.taxNote()}</div>
                </div>
            `;
        }
    } else {
        // Subscription price display. Protocols are one bundled delivery;
        // formulas show the complete delivery total before the per-box value.
        if (this.isBundle) {
            mainPriceContainer.innerHTML = `
                <div class="price__subscription-display">
                    <div class="price__current-sub" style="font-weight: bold;">
                        ${this.formatMoney(finalPrice)} <span class="per-box">/ bundle</span>
                    </div>
                    <div class="price__was-sub" style="text-decoration: line-through; color: #888; margin-left: 10px;">
                        ${this.formatMoney(this.bundleOriginalPrice || unitPrice)}
                    </div>
                    <div class="price__tax-note">${this.taxNote()}</div>
                </div>
            `;
        } else {
            const quantity = this.selectedQuantity || 1;
            const deliveryDays = this.isGlow ? this.getGlowPackSize() : (15 * quantity);
            const deliveryTotal = finalPrice * quantity;
            const compareAtTotal = unitPrice * quantity;
            const boxBreakdown = quantity === 1
                ? `1 box · ${this.formatMoney(finalPrice)} per box`
                : `${quantity} boxes · ${this.formatMoney(finalPrice)} per box`;

            mainPriceContainer.innerHTML = `
                <div class="price__subscription-display price__subscription-display--delivery">
                    <div class="price__current-sub" style="font-weight: bold;">
                        ${this.formatMoney(deliveryTotal)} <span class="per-box">/ delivery</span>
                    </div>
                    <div class="price__was-sub" style="text-decoration: line-through; color: #888; margin-left: 10px;">
                        ${this.formatMoney(compareAtTotal)}
                    </div>
                    <div class="beme-price-breakdown">${boxBreakdown}</div>
                    <div class="price__tax-note">${this.taxNote()}</div>
                </div>
            `;
        }
    }
  }

  formatMoney(cents) {
    // Divide by 100 because Shopify sends prices in cents
    return this.currencyFormatter.format(cents / 100);
  }
}

customElements.define('purchase-options-component', PurchaseOptionsComponent);


